import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, BarChart3, Receipt, ArrowUpRight, RefreshCw, Download } from "lucide-react";
import { toast } from "sonner";

const STATUS_BADGE = {
  draft: "bg-gray-100 text-gray-600",
  sent: "bg-blue-100 text-blue-700",
  partially_paid: "bg-amber-100 text-amber-700",
  paid: "bg-emerald-100 text-emerald-800",
  overdue: "bg-rose-100 text-rose-700",
  voided: "bg-gray-100 text-gray-500 line-through",
};

const STATUS_LABEL = {
  draft: "Draft",
  sent: "Sent",
  partially_paid: "Partial",
  paid: "Paid",
  overdue: "Overdue",
  voided: "Voided",
};

const fmtMoney = (n, ccy = "GBP") => {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: ccy }).format(n || 0);
  } catch {
    return `${ccy} ${(n || 0).toFixed(2)}`;
  }
};

const downloadCsv = async (url, filename) => {
  const token = localStorage.getItem("token");
  const BASE = process.env.REACT_APP_API_URL || "";
  const res = await fetch(`${BASE}/api${url}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error("Export failed");
  const blob = await res.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
};

const todayIso = () => new Date().toISOString().slice(0, 10);
const addDaysIso = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const emptyLineItem = { description: "", quantity: 1, unit_price: 0 };
const newLineItem = () => ({ ...emptyLineItem, _uid: `li_${Math.random().toString(36).slice(2, 11)}` });

const Invoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    project_id: "",
    deal_id: "",
    due_date: addDaysIso(30),
    issue_date: todayIso(),
    tax_rate: 20,
    purchase_order_number: "",
    notes: "",
    line_items: [newLineItem()],
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [inv, proj, dl] = await Promise.all([
        api.get("/invoices"),
        api.get("/projects"),
        api.get("/deals").catch(() => ({ data: [] })),
      ]);
      setInvoices(inv.data);
      setProjects(proj.data);
      setDeals(dl.data);
    } catch (e) {
      toast.error("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-open the new-invoice dialog when arrived via "New invoice" (?new=1)
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setOpen(true);
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const totals = invoices.reduce(
    (acc, i) => {
      acc.outstanding += i.status === "paid" || i.status === "voided" ? 0 : i.balance_due;
      acc.collected += i.amount_paid;
      acc.invoiced += i.status === "voided" ? 0 : i.total_amount;
      return acc;
    },
    { outstanding: 0, collected: 0, invoiced: 0 }
  );

  const updateLineItem = (idx, field, value) => {
    const items = [...form.line_items];
    items[idx] = { ...items[idx], [field]: value };
    setForm({ ...form, line_items: items });
  };

  const addLineItem = () => setForm({ ...form, line_items: [...form.line_items, newLineItem()] });

  const removeLineItem = (idx) => {
    const items = form.line_items.filter((_, i) => i !== idx);
    setForm({ ...form, line_items: items.length ? items : [newLineItem()] });
  };

  const previewTotals = form.line_items.reduce(
    (acc, li) => {
      const total = (Number(li.quantity) || 0) * (Number(li.unit_price) || 0);
      acc.subtotal += total;
      return acc;
    },
    { subtotal: 0 }
  );
  previewTotals.tax = previewTotals.subtotal * ((Number(form.tax_rate) || 0) / 100);
  previewTotals.total = previewTotals.subtotal + previewTotals.tax;

  const create = async () => {
    if (!form.project_id) return toast.error("Choose a project");
    if (!form.due_date) return toast.error("Set a due date");
    if (!form.line_items.some((li) => li.description.trim())) return toast.error("Add at least one line item");

    setBusy(true);
    try {
      const payload = {
        project_id: form.project_id,
        deal_id: form.deal_id || null,
        due_date: form.due_date,
        issue_date: form.issue_date,
        tax_rate: (Number(form.tax_rate) || 0) / 100,
        purchase_order_number: form.purchase_order_number,
        notes: form.notes,
        line_items: form.line_items
          .filter((li) => li.description.trim())
          .map((li) => ({
            description: li.description.trim(),
            quantity: Number(li.quantity) || 0,
            unit_price: Number(li.unit_price) || 0,
          })),
      };
      await api.post("/invoices", payload);
      toast.success("Invoice drafted");
      setOpen(false);
      setForm({
        project_id: "",
        deal_id: "",
        due_date: addDaysIso(30),
        issue_date: todayIso(),
        tax_rate: 20,
        purchase_order_number: "",
        notes: "",
        line_items: [newLineItem()],
      });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create invoice");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Finance"
        title="Invoices"
        description="Track billings, payments and aging across all projects. Mirrors PMO billing best-practice."
        actions={
          <div className="flex gap-2">
            <Link to="/invoices/recurring">
              <Button variant="outline" className="rounded-none border-gray-300 text-gray-600 hover:text-gray-900 bg-transparent" data-testid="open-recurring-btn">
                <RefreshCw className="w-4 h-4 mr-1" /> Recurring
              </Button>
            </Link>
            <Link to="/invoices/aging">
              <Button variant="outline" className="rounded-none border-gray-300 text-gray-600 hover:text-gray-900 bg-transparent" data-testid="open-aging-report-btn">
                <BarChart3 className="w-4 h-4 mr-1" /> Aging report
              </Button>
            </Link>
            <Button
              variant="outline"
              onClick={() => downloadCsv("/invoices/export", "invoices.csv")}
              className="rounded-none border-gray-300 text-gray-600 hover:text-gray-900 bg-transparent"
              data-testid="export-invoices-btn"
            >
              <Download className="w-4 h-4 mr-1" /> Export CSV
            </Button>
            <Button
              onClick={() => setOpen(true)}
              className="rounded-none bg-[#002FA7] hover:bg-[#002480]"
              data-testid="new-invoice-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> New invoice
            </Button>
          </div>
        }
      />

      {/* KPI strip */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#1F2937] border-b border-[#1F2937]">
        <div className="bg-[#0D1117] px-5 sm:px-8 lg:px-12 py-5">
          <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-500">Total invoiced</div>
          <div className="font-mono font-bold text-3xl tracking-tight mt-2" data-testid="kpi-invoiced">{fmtMoney(totals.invoiced)}</div>
        </div>
        <div className="bg-[#0D1117] px-5 sm:px-8 lg:px-12 py-5">
          <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-500">Collected</div>
          <div className="font-mono font-bold text-3xl tracking-tight mt-2 text-emerald-600 dark:text-emerald-400" data-testid="kpi-collected">{fmtMoney(totals.collected)}</div>
        </div>
        <div className="bg-[#0D1117] px-5 sm:px-8 lg:px-12 py-5">
          <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-500">Outstanding</div>
          <div className="font-mono font-bold text-3xl tracking-tight mt-2 text-rose-600 dark:text-rose-400" data-testid="kpi-outstanding">{fmtMoney(totals.outstanding)}</div>
        </div>
      </div>

      <div className="px-5 sm:px-8 lg:px-12 py-8">
        {loading ? (
          <div className="text-sm text-gray-500">Loading invoices…</div>
        ) : invoices.length === 0 ? (
          <div className="border border-dashed border-[#374151] p-16 text-center">
            <Receipt className="w-8 h-8 mx-auto text-gray-400" strokeWidth={1.4} />
            <div className="font-display text-xl mt-3 tracking-tight">No invoices yet</div>
            <p className="text-sm text-gray-500 mt-1">Bill your first piece of work — track AR, aging and payments.</p>
            <Button
              onClick={() => setOpen(true)}
              className="rounded-none bg-[#002FA7] hover:bg-[#002480] mt-5"
              data-testid="empty-create-invoice-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> New invoice
            </Button>
          </div>
        ) : (
          <div className="border border-[#1F2937] overflow-x-auto">
            <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-[#1F2937] bg-[#030712] text-[10px] uppercase tracking-[0.25em] text-gray-500 min-w-[480px]">
              <div className="col-span-2">Number</div>
              <div className="col-span-3">Client / Project</div>
              <div className="col-span-2">Due</div>
              <div className="col-span-2 text-right">Total</div>
              <div className="hidden sm:block col-span-2 text-right">Balance</div>
              <div className="col-span-1 text-right">Status</div>
            </div>
            {invoices.map((inv) => {
              const proj = projects.find((p) => p.project_id === inv.project_id);
              return (
                <Link
                  key={inv.invoice_id}
                  to={`/invoices/${inv.invoice_id}`}
                  className="grid grid-cols-12 gap-4 px-5 py-3.5 border-b border-[#1F2937] hover:bg-[#030712] items-center text-sm min-w-[480px]"
                  data-testid={`invoice-row-${inv.invoice_id}`}
                >
                  <div className="col-span-2 font-mono text-xs flex items-center gap-1">
                    {inv.invoice_number}
                    <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100" />
                  </div>
                  <div className="col-span-3 min-w-0">
                    <div className="truncate font-medium">{inv.client_name || proj?.name || "—"}</div>
                    <div className="truncate text-[11px] text-gray-500">{proj?.name}</div>
                  </div>
                  <div className="col-span-2 font-mono text-xs text-gray-500">
                    {inv.due_date ? new Date(inv.due_date).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—"}
                  </div>
                  <div className="col-span-2 text-right font-mono">{fmtMoney(inv.total_amount, inv.currency)}</div>
                  <div className={`hidden sm:block col-span-2 text-right font-mono ${inv.balance_due > 0 ? "text-rose-700" : "text-emerald-700"}`}>
                    {fmtMoney(inv.balance_due, inv.currency)}
                  </div>
                  <div className="col-span-1 text-right">
                    <span
                      className={`text-[10px] uppercase tracking-[0.18em] px-2 py-1 ${STATUS_BADGE[inv.status] || STATUS_BADGE.draft}`}
                      data-testid={`invoice-status-${inv.invoice_id}`}
                    >
                      {STATUS_LABEL[inv.status] || inv.status}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none bg-[#111827] border-[#374151] text-white sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="invoice-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">New invoice</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-[0.2em]">Project *</Label>
                <select
                  value={form.project_id}
                  onChange={(e) => setForm({ ...form, project_id: e.target.value })}
                  className="w-full rounded-none mt-1 h-10 border border-[#374151] bg-[#0D1117] text-white px-3 text-sm"
                  data-testid="invoice-project-select"
                >
                  <option value="">— pick a project —</option>
                  {projects.map((p) => (
                    <option key={p.project_id} value={p.project_id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.2em]">Client deal</Label>
                <select
                  value={form.deal_id}
                  onChange={(e) => setForm({ ...form, deal_id: e.target.value })}
                  className="w-full rounded-none mt-1 h-10 border border-[#374151] bg-[#0D1117] text-white px-3 text-sm"
                  data-testid="invoice-deal-select"
                >
                  <option value="">— no deal —</option>
                  {deals.map((d) => (
                    <option key={d.deal_id} value={d.deal_id}>{d.name} ({d.client})</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-[0.2em]">Issue date</Label>
                <Input type="date" className="rounded-none mt-1" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} data-testid="invoice-issue-date" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.2em]">Due date *</Label>
                <Input type="date" className="rounded-none mt-1" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="invoice-due-date" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.2em]">PO number</Label>
                <Input className="rounded-none mt-1" placeholder="optional" value={form.purchase_order_number} onChange={(e) => setForm({ ...form, purchase_order_number: e.target.value })} data-testid="invoice-po-number" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.2em]">Tax rate</Label>
                <div className="relative mt-1">
                  <Input type="number" step="1" min="0" max="100" className="rounded-none pr-7" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} data-testid="invoice-tax-rate" />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">%</span>
                </div>
              </div>
            </div>

            {/* Line items */}
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-slate-400 mb-2">Line items</div>
              <div className="border border-[#1F2937] divide-y divide-[#1F2937]">
                <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-[#030712] text-[10px] uppercase tracking-[0.2em] text-gray-500">
                  <div className="col-span-6">Description</div>
                  <div className="col-span-2 text-right">Qty</div>
                  <div className="col-span-2 text-right">Unit price</div>
                  <div className="col-span-1 text-right">Total</div>
                  <div className="col-span-1"></div>
                </div>
                {form.line_items.map((li, idx) => (
                  <div key={li._uid || `legacy-${idx}`} className="grid grid-cols-12 gap-2 px-3 py-2 items-center" data-testid={`invoice-line-row-${idx}`}>
                    <Input
                      className="col-span-6 rounded-none h-9"
                      placeholder="e.g. Senior PM — June"
                      value={li.description}
                      onChange={(e) => updateLineItem(idx, "description", e.target.value)}
                      data-testid={`invoice-line-desc-${idx}`}
                    />
                    <Input
                      type="number"
                      step="0.5"
                      className="col-span-2 rounded-none h-9 text-right font-mono"
                      value={li.quantity}
                      onChange={(e) => updateLineItem(idx, "quantity", e.target.value)}
                      data-testid={`invoice-line-qty-${idx}`}
                    />
                    <Input
                      type="number"
                      step="0.01"
                      className="col-span-2 rounded-none h-9 text-right font-mono"
                      value={li.unit_price}
                      onChange={(e) => updateLineItem(idx, "unit_price", e.target.value)}
                      data-testid={`invoice-line-price-${idx}`}
                    />
                    <div className="col-span-1 text-right font-mono text-sm" data-testid={`invoice-line-total-${idx}`}>
                      {((Number(li.quantity) || 0) * (Number(li.unit_price) || 0)).toFixed(2)}
                    </div>
                    <button
                      onClick={() => removeLineItem(idx)}
                      className="col-span-1 text-gray-400 hover:text-rose-600 justify-self-end"
                      data-testid={`invoice-line-remove-${idx}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
              <Button
                onClick={addLineItem}
                variant="ghost"
                size="sm"
                className="rounded-none mt-2 text-xs"
                data-testid="invoice-add-line-btn"
              >
                <Plus className="w-3 h-3 mr-1" /> Add line item
              </Button>
            </div>

            {/* Totals preview */}
            <div className="bg-[#030712] border border-[#1F2937] p-4 flex justify-end" data-testid="invoice-preview-totals">
              <div className="text-sm space-y-1 font-mono min-w-[260px]">
                <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span>{previewTotals.subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Tax ({Number(form.tax_rate).toFixed(0)}%)</span><span>{previewTotals.tax.toFixed(2)}</span></div>
                <div className="flex justify-between font-display font-bold text-base pt-1 border-t border-[#374151]"><span>Total</span><span>{previewTotals.total.toFixed(2)}</span></div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={create}
              disabled={busy}
              className="rounded-none bg-[#002FA7] hover:bg-[#002480]"
              data-testid="confirm-create-invoice-btn"
            >
              {busy ? "Creating…" : "Create as draft"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Invoices;
